import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, Scale, Search } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { GlobalSearchBar } from "@/components/GlobalSearchBar";

const links = [
  { to: "/banks", label: "All Banks" },
  { to: "/compare", label: "Compare Banks" },
  { to: "/calculator", label: "Travel Fee Calculator" },
  { to: "/rankings", label: "Rankings" },
  { to: "/about", label: "About" },
  { to: "/partners", label: "Institutional" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-[#FCFCFD]/95 backdrop-blur-md border-b border-black/[0.08]">
      <div className="max-w-7xl mx-auto px-6 min-[1360px]:px-10 h-20 flex items-center justify-between gap-3 min-[1360px]:gap-6">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group shrink-0">
          <Scale size={22} className="text-[#059669]" strokeWidth={2.5} />
          <span className="font-display font-black text-2xl tracking-tighter text-[#0F1115]">
            Bankingfees<span className="text-[#059669]">.eu</span>
          </span>
        </Link>
        <nav className="hidden min-[1360px]:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}-link`}
              className={({ isActive }) =>
                `whitespace-nowrap text-sm font-medium tracking-wide transition-colors duration-200 hover:text-[#059669] ${
                  isActive ? "text-[#059669]" : "text-[#0F1115]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden min-[1360px]:block w-px h-6 bg-black/[0.08] shrink-0" />
        <div className="hidden min-[1360px]:flex items-center gap-5 shrink-0">
          <button
            type="button"
            data-testid="nav-search-toggle-button"
            onClick={() => setSearchOpen((v) => !v)}
            aria-label="Search Bankingfees.eu"
            className={`inline-flex items-center justify-center h-11 w-11 shrink-0 transition-all duration-200 hover:scale-105 active:scale-95 ${
              searchOpen ? "bg-[#0F1115] text-white shadow-md" : "bg-[#059669] text-white shadow-sm shadow-[#059669]/30 hover:bg-[#047857] hover:shadow-md hover:shadow-[#059669]/40"
            }`}
          >
            <Search size={18} strokeWidth={2.25} />
          </button>
          <Link
            to="/private-banking"
            data-testid="nav-private-banking-link"
            className="inline-flex items-center justify-center h-11 gap-1.5 whitespace-nowrap border border-[#D4AF37]/50 text-[#8A6D1E] px-4 text-sm font-semibold hover:bg-[#D4AF37] hover:text-[#090A0C] hover:border-[#D4AF37] transition-colors duration-200"
          >
            Private Banking
          </Link>
          <Link
            to="/compare"
            data-testid={NAV.ctaButton}
            className="inline-flex items-center justify-center h-11 gap-1.5 whitespace-nowrap bg-[#0F1115] text-white px-5 text-sm font-semibold rounded-none hover:bg-[#059669] transition-colors duration-200"
          >
            Find My Bank <ArrowUpRight size={15} />
          </Link>
        </div>
        <div className="min-[1360px]:hidden flex items-center gap-2">
          <button
            type="button"
            data-testid="nav-mobile-search-toggle-button"
            onClick={() => {
              setSearchOpen((v) => !v);
              setOpen(false);
            }}
            aria-label="Search Bankingfees.eu"
            className={`inline-flex items-center justify-center h-10 w-10 shrink-0 transition-all duration-200 active:scale-95 ${
              searchOpen ? "bg-[#0F1115] text-white" : "bg-[#059669] text-white shadow-sm shadow-[#059669]/30"
            }`}
          >
            <Search size={19} strokeWidth={2.25} />
          </button>
          <button
            data-testid={NAV.mobileMenuToggle}
            aria-label="Toggle navigation menu"
            className="inline-flex items-center justify-center h-10 w-10 shrink-0 text-[#0F1115]"
            onClick={() => {
              setOpen((v) => !v);
              setSearchOpen(false);
            }}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {searchOpen && (
        <>
          <div
            data-testid="nav-search-backdrop"
            onClick={() => setSearchOpen(false)}
            className="fixed inset-0 top-20 z-40 bg-[#0F1115]/30 backdrop-blur-[2px]"
          />
          <div className="absolute left-0 right-0 top-full z-50 border-t border-black/[0.08] bg-white px-6 min-[1360px]:px-10 py-5 shadow-lg">
            <div className="max-w-2xl mx-auto">
              <GlobalSearchBar
                testIdPrefix="navbar"
                enableShortcuts={false}
                autoFocusOnMount
                showKbdHint={false}
                onNavigate={() => setSearchOpen(false)}
              />
            </div>
          </div>
        </>
      )}
      {open && (
        <div className="min-[1360px]:hidden border-t border-black/[0.08] bg-white px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-base font-medium text-[#0F1115]">
              {l.label}
            </Link>
          ))}
          <Link
            to="/private-banking"
            data-testid="nav-mobile-private-banking-link"
            onClick={() => setOpen(false)}
            className="text-base font-medium text-[#8A6D1E]"
          >
            Private Banking
          </Link>
        </div>
      )}
    </header>
  );
};
